import React from 'react';

import kebabCase from 'lodash/kebabCase';

import { Button, ButtonLink } from '@change-corgi/design-system-legacy/components/actions';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { useCTAButtonVariant } from 'src/app/pages/starterDashboardV2/shared/contexts';
import { useTrackView } from 'src/app/shared/hooks/tracking';

import type { TaskShape } from '../../../../types';

type ActiveTaskProps = Omit<TaskShape, 'status'>;
export function ActiveTask({
	name,
	title,
	description,
	icon,
	primaryActionText,
	primaryActionHref,
	primaryActionOnClick,
}: ActiveTaskProps): React.JSX.Element {
	useTrackView('petition_manage_tasks_view', { task: name });
	const { getCTAVariant } = useCTAButtonVariant();

	return (
		<Box data-qa="petition-task">
			<Flex sx={{ gap: [16, 24] }}>
				<Flex
					sx={{
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: 'primary-greyBackground',
						borderRadius: 'full',
						height: 40,
						width: 40,
						flexShrink: 0,
					}}
				>
					<Icon icon={icon} size={20} color="secondary-blue" />
				</Flex>
				<Flex
					sx={{
						flexDirection: ['column', 'row'],
						alignItems: ['flex-start', 'center'],
						justifyContent: [null, 'space-between'],
						gap: [16, 24],
						width: '100%',
					}}
				>
					<Flex sx={{ flexDirection: 'column', gap: 8 }}>
						<Heading data-qa={`task-title-${kebabCase(name)}`} as="h3" size="h5">
							{title}
						</Heading>
						<Text as="p" data-qa={`task-description-${kebabCase(name)}`} color="typography-secondary">
							{description}
						</Text>
					</Flex>
					<Box sx={{ minWidth: 170, flexShrink: 0 }}>
						{primaryActionHref ? (
							<ButtonLink
								variant={getCTAVariant('secondaryEmphasis')}
								size="medium"
								to={primaryActionHref}
								onClick={primaryActionOnClick}
								data-qa={`task-action-button-${kebabCase(name)}`}
								sx={{ width: '100%' }}
							>
								{primaryActionText}
							</ButtonLink>
						) : (
							<Button
								variant={getCTAVariant('secondaryEmphasis')}
								size="medium"
								onClick={primaryActionOnClick}
								data-qa={`task-action-button-${kebabCase(name)}`}
								sx={{ width: '100%' }}
							>
								{primaryActionText}
							</Button>
						)}
					</Box>
				</Flex>
			</Flex>
		</Box>
	);
}
